import { useState } from "react";
import { useFallbackRule, usePresets, useUpdateFallbackRule } from "../api/queries";
import { ApiError } from "../api/client";

export default function FallbackRuleCard() {
  const { data, isLoading, isError } = useFallbackRule();
  const presets = usePresets();
  const updateMut = useUpdateFallbackRule();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (isLoading) return <p className="text-sm text-slate-400">Loading fallback rule…</p>;
  if (isError || !data) return <p className="text-sm text-rose-400">Failed to load fallback rule.</p>;

  const fb = data.fallback;

  async function save(body: {
    enabled?: boolean;
    action_kind?: "preset" | "do_nothing";
    preset_id?: number | null;
    clear_preset?: boolean;
  }) {
    setError(null);
    setSaving(true);
    try {
      await updateMut.mutateAsync(body);
    } catch (err) {
      if (err instanceof ApiError) setError(formatApiDetail(err.detail) || err.message);
      else if (err instanceof Error) setError(err.message);
      else setError("save failed");
    } finally {
      setSaving(false);
    }
  }

  function formatApiDetail(detail: unknown): string {
    if (detail == null) return "";
    if (typeof detail === "string") return detail;
    try {
      return JSON.stringify(detail);
    } catch {
      return String(detail);
    }
  }

  return (
    <div className="rounded-2xl border border-amber-700/40 bg-amber-950/20 p-4 space-y-3">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-amber-100">Fallback rule</h3>
          <p className="text-xs text-amber-200/70">
            Runs when no other rule matched the tick. Cannot be deleted; toggle enabled to skip it.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-amber-100">
          <input
            type="checkbox"
            checked={fb.enabled}
            disabled={saving}
            onChange={(e) => void save({ enabled: e.target.checked })}
            className="h-4 w-4 accent-amber-400"
          />
          enabled
        </label>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-amber-200/70">Action</div>
          <div className="flex gap-2">
            {(["preset", "do_nothing"] as const).map((k) => (
              <button
                key={k}
                type="button"
                disabled={saving}
                onClick={() =>
                  void save(k === "do_nothing" ? { action_kind: k, clear_preset: true } : { action_kind: k })
                }
                className={`rounded-md border px-3 py-1 text-sm ${
                  fb.action_kind === k
                    ? "border-amber-400 bg-amber-400/20 text-amber-100"
                    : "border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-600"
                }`}
              >
                {k}
              </button>
            ))}
          </div>
        </div>
        {fb.action_kind === "preset" ? (
          <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-amber-200/70">Preset</div>
            <select
              className="input"
              value={fb.preset_id ?? ""}
              disabled={saving}
              onChange={(e) => void save({ preset_id: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">— choose —</option>
              {(presets.data?.presets ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.color} · lum {p.luminance} · {p.effect}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {error ? <div className="text-sm text-rose-400">{error}</div> : null}
    </div>
  );
}
